/**
 * A question at work (the Ask page sketch, after the explainer video): on the left what Ask reads
 * as it reads it (the skill, each query by its purpose with what it found, the number check),
 * each row joined by a line to the orb; the orb says what it does and how much it has read; on the
 * right the answer card. A finished question keeps the same picture, the lines quiet. Below 980px
 * of its own width it stacks and the lines go (ask.css).
 *
 * The lines are measured from the rows as they are laid out (a row's right edge to the orb's left
 * edge, the orb's right edge to the card), again whenever the rows or the width change.
 */
import { useLayoutEffect, useRef, useState } from 'react'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { cx } from '@/kit/cx'
import type { AskStep } from './askApi'
import { activeStep, countsOf } from './askRun'
import type { AskRun } from './askRun'
import { AnswerCard } from './AnswerCard'
import { makeAskFormats } from './format'
import { Orb } from './Mark'
import './ask.css'

interface Row {
    key: string
    kind: 'skill' | 'data' | 'check'
    label: string
    value: string
    state: 'running' | 'done' | 'failed' | 'warn'
    tip?: string
}

function rowsOf(run: AskRun, t: TFunction, figures: (step: AskStep) => string | null): { section: 'skill' | 'data' | 'check'; rows: Row[] }[] {
    const skill = run.steps
        .filter((s) => s.tool === 'load_skill')
        .map((s): Row => ({ key: `s${s.id}`, kind: 'skill', label: s.title || s.name || '', value: s.state === 'failed' ? t('ask.stepFailed') : t('ask.skillRead'), state: s.state === 'failed' ? 'failed' : 'done' }))
    const data = run.steps
        .filter((s) => s.tool === 'query')
        .map((s): Row => ({
            key: `q${s.id}`,
            kind: 'data',
            label: s.purpose || '',
            value: s.state === 'running' ? t('ask.reading') : s.state === 'failed' ? t('ask.stepFailed') : (figures(s) ?? t('ask.count.rows', { count: s.rows ?? 0 })),
            state: s.state,
            tip: s.state === 'done' ? `${(s.views ?? []).join(', ')} · ${t('ask.count.rows', { count: s.rows ?? 0 })}` : undefined,
        }))
    const check: Row[] = []
    if (run.numberCheck && run.numberCheck.checked > 0) {
        const ok = run.numberCheck.checked - run.numberCheck.unverified.length
        check.push({ key: 'check', kind: 'check', label: t('ask.check.label'), value: `${ok} / ${run.numberCheck.checked}`, state: run.numberCheck.ok ? 'done' : 'warn' })
    } else if (run.stage === 'check' && run.status === 'running') {
        check.push({ key: 'check', kind: 'check', label: t('ask.check.label'), value: t('ask.check.running'), state: 'running' })
    }
    return [
        { section: 'skill' as const, rows: skill },
        { section: 'data' as const, rows: data },
        { section: 'check' as const, rows: check },
    ].filter((s) => s.rows.length > 0)
}

function RowIcon({ kind, state }: { kind: Row['kind']; state: Row['state'] }) {
    const color = state === 'warn' ? 'var(--text-warning)' : kind === 'check' && state === 'done' ? 'var(--text-success)' : 'var(--accent-default)'
    return (
        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
            {kind === 'skill' && (
                <>
                    <path d="M4 5h5a3 3 0 0 1 3 3v12a2 2 0 0 0-2-2H4z" />
                    <path d="M20 5h-5a3 3 0 0 0-3 3v12a2 2 0 0 1 2-2h6z" />
                </>
            )}
            {kind === 'data' && <path d="M5 20v-7M10 20V6M15 20v-10M20 20v-4" />}
            {kind === 'check' && (
                <>
                    <path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z" />
                    <path d="M9 12l2 2 4-4" />
                </>
            )}
        </svg>
    )
}

interface Lines {
    width: number
    height: number
    paths: { key: string; d: string; active: boolean }[]
    toCard: { x1: number; x2: number; y: number } | null
}

export function AskWork({ run, live, onAsk }: { run: AskRun; live: boolean; onAsk?: (question: string) => void }) {
    const { t, i18n } = useTranslation()
    const fmt = makeAskFormats(i18n.language)
    const sections = rowsOf(run, t, fmt.figures)
    const counts = countsOf(run.steps)
    const running = run.status === 'running'
    const stage = running ? t(`ask.stage.${run.stage}`) : run.status === 'answered' ? t('ask.stage.done') : t('ask.stage.stopped')
    const active = activeStep(run.steps)

    const gridRef = useRef<HTMLDivElement>(null)
    const readsRef = useRef<HTMLDivElement>(null)
    const orbRef = useRef<HTMLDivElement>(null)
    const cardRef = useRef<HTMLDivElement>(null)
    const rowRefs = useRef(new Map<string, HTMLDivElement>())
    const [lines, setLines] = useState<Lines | null>(null)
    const rowKeys = sections.flatMap((s) => s.rows.map((r) => `${r.key}:${r.state}`)).join('|')

    useLayoutEffect(() => {
        const reads = readsRef.current
        const grid = gridRef.current
        if (!reads || !grid) return undefined
        const measure = () => {
            const orb = orbRef.current?.getBoundingClientRect()
            const box = reads.getBoundingClientRect()
            if (!orb || orb.width === 0 || getComputedStyle(reads.querySelector('.ask-lines') ?? reads).display === 'none') {
                setLines(null)
                return
            }
            const ox = orb.left - box.left
            const oy = orb.top - box.top + orb.height / 2
            const paths: Lines['paths'] = []
            for (const [key, el] of rowRefs.current) {
                const r = el.getBoundingClientRect()
                if (r.width === 0) continue
                const x = r.right - box.left
                const y = r.top - box.top + r.height / 2
                const bend = Math.max(24, (ox - x) * 0.55)
                paths.push({ key, d: `M${x} ${y} C ${x + bend} ${y}, ${ox - bend} ${oy}, ${ox} ${oy}`, active: el.dataset.state === 'running' })
            }
            const card = cardRef.current?.getBoundingClientRect()
            // To the card once there is one (the answer), beside the orb.
            const toCard = run.answer && card && card.width > 0 && card.left > orb.right ? { x1: orb.right - box.left, x2: card.left - box.left, y: oy } : null
            setLines({ width: box.width, height: box.height, paths, toCard })
        }
        measure()
        const observer = new ResizeObserver(measure)
        observer.observe(grid)
        observer.observe(reads)
        // Answered, the orb shrinks by a transform, which no resize reports: measured at its end.
        grid.addEventListener('transitionend', measure)
        return () => {
            observer.disconnect()
            grid.removeEventListener('transitionend', measure)
        }
    }, [rowKeys, run.answer])

    const setRow = (key: string) => (el: HTMLDivElement | null) => {
        if (el) rowRefs.current.set(key, el)
        else rowRefs.current.delete(key)
    }

    return (
        <div className="ask-work w-full min-w-0" data-testid="ask-work">
            <div ref={gridRef} className={cx('ask-work-grid', run.answer && 'is-answered')}>
                <div ref={readsRef} className="ask-reads">
                    {lines && (
                        <svg className="ask-lines" aria-hidden="true" width={lines.width} height={lines.height} viewBox={`0 0 ${lines.width} ${lines.height}`} fill="none">
                            {lines.paths.map((p) => (
                                <path key={p.key} d={p.d} stroke={p.active ? 'var(--accent-default)' : '#d6d6de'} strokeWidth={p.active ? 1.5 : 1} className={p.active ? 'ask-flow' : undefined} />
                            ))}
                        </svg>
                    )}
                    <div className="flex min-w-0 flex-col" aria-label={t('ask.section.data')}>
                        {sections.map((section) => {
                            const busy = section.rows.some((r) => r.state === 'running')
                            return (
                                <div key={section.section} className="mb-[8px] flex flex-col">
                                    <div className={cx('mb-[6px] flex h-[20px] items-end text-[11px] leading-[14px] font-[650] tracking-[0.06em] uppercase transition-colors', busy ? 'text-[var(--accent-default)]' : 'text-[var(--text-weaker)]')}>{t(`ask.section.${section.section}`)}</div>
                                    {section.rows.map((row) => (
                                        <div
                                            key={row.key}
                                            ref={setRow(row.key)}
                                            data-state={row.state}
                                            title={row.tip}
                                            className={cx(
                                                'mb-[6px] flex min-h-[32px] items-center gap-[8px] rounded-[8px] border border-solid border-[var(--border-default)] px-[10px] py-[6px] transition-colors duration-300',
                                                row.state === 'running' ? 'bg-[var(--accent-weak)]' : 'bg-[var(--surface-raised)]',
                                                live && 'ask-in',
                                            )}
                                        >
                                            <RowIcon kind={row.kind} state={row.state} />
                                            <span className="min-w-0 flex-1 truncate text-[12.5px] leading-[18px] font-normal text-[var(--text-default)]">{row.label}</span>
                                            <span
                                                className={cx(
                                                    'text-[12.5px] leading-[18px] font-[650] whitespace-nowrap tabular-nums',
                                                    row.state === 'running' && 'ask-reading text-[var(--text-weaker)]',
                                                    row.state === 'failed' && 'font-normal text-[var(--text-weaker)]',
                                                    row.state === 'warn' && 'text-[var(--text-warning)]',
                                                    row.state === 'done' && (row.kind === 'check' ? 'text-[var(--text-success)]' : 'text-[var(--text-default)]'),
                                                )}
                                            >
                                                {row.value}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )
                        })}
                    </div>
                    <div className="ask-orb-col">
                        <div className="ask-orb-pin">
                            <div ref={orbRef} className="ask-orb-wrap inline-flex">
                                <Orb size={64} calm={!running} />
                            </div>
                            <div className="ask-orb-caption" role="status" aria-live="polite">
                                <div className="text-[13px] leading-[18px] font-[650] text-[var(--text-default)]">{running && active?.purpose ? `${stage}: ${active.purpose}` : stage}</div>
                                <div className="mt-[2px] text-[11.5px] leading-[16px] font-normal text-[var(--text-weaker)]">
                                    {[t('ask.count.queries', { count: counts.queries }), t('ask.count.rows', { count: counts.rows }), t('ask.count.views', { count: counts.views })].join(' · ')}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div ref={cardRef} className="min-w-0">
                    {run.answer ? (
                        <AnswerCard answer={run.answer} numberCheck={run.numberCheck} steps={run.steps} animate={live} onAsk={onAsk} />
                    ) : !running ? (
                        <p className="m-0 rounded-[12px] border border-solid border-[var(--border-default)] px-[16px] py-[14px] text-[14px] leading-[20px] font-normal text-[var(--text-weak)]" role="alert">
                            {t(`ask.error.${run.error && ['over_budget', 'not_configured', 'forbidden_by_role', 'no_answer'].includes(run.error) ? run.error : run.status === 'stopped' ? 'stopped' : 'failed'}`)}
                        </p>
                    ) : null}
                </div>
                {lines?.toCard && (
                    <svg aria-hidden="true" className="pointer-events-none absolute top-0 left-0 overflow-visible" width="1" height="1">
                        <path d={`M${lines.toCard.x1} ${lines.toCard.y} L${lines.toCard.x2} ${lines.toCard.y}`} stroke="var(--accent-default)" strokeWidth={1.5} />
                        <circle cx={lines.toCard.x2} cy={lines.toCard.y} r={3} fill="var(--accent-default)" />
                    </svg>
                )}
            </div>
        </div>
    )
}
